"use client";

import { ImageGallery, ImageGalleryCategory, ImageGalleryImage, useImageGallery } from "@/components/common/gallery";
import {
	GalleryCategories,
	GalleryDots,
	GalleryImageCounter,
	GalleryInfoButton,
} from "@/components/common/gallery/overlays";
import { SUPType } from "@/types/sup";
import { prepareGalleryImages } from "@/utils/sup/gallery";
import { unitSpaces } from "@/utils/sup/sort-media-list";

import * as React from "react";

interface UnitImageGalleryProps {
	images: { url: string; space: string }[];
	categories?: { name: string; code: string }[];
	shouldPrioritizeCoverImage?: boolean;
	sizes?: string;
	coverImage?: string;
	projectId?: string;
	type?: SUPType;
}

const defaultCategories = [
	{ name: "Hall", code: "halls" },
	{ name: "Bedroom", code: "bedrooms" },
	{ name: "Balcony", code: "balconies" },
	{ name: "Kitchen", code: "kitchens" },
	{ name: "Amenities", code: "amenities" },
	{ name: "Bathroom", code: "bathrooms" },
	{ name: "Others", code: "others" },
	{ name: "Parking", code: "parkings" },
	{ name: "Pooja Room", code: "poojaRooms" },
	{ name: "Utility", code: "utilities" },
];

// Internal component that uses gallery context to sync category
function GalleryContent({
	images,
	sortedCategories,
	selectedCategory,
	setSelectedCategory,
}: {
	images: ImageGalleryImage[];
	sortedCategories: ImageGalleryCategory[];
	selectedCategory: ImageGalleryCategory | undefined;
	setSelectedCategory: (category: ImageGalleryCategory | undefined) => void;
}) {
	const { currentIndex } = useImageGallery();

	// Sync selected category with current image
	React.useEffect(() => {
		const currentImage = images[currentIndex];
		if (currentImage?.space) {
			const currentCategory = sortedCategories.find(cat => cat.code === currentImage.space);
			if (currentCategory && currentCategory.code !== selectedCategory?.code) {
				setSelectedCategory(currentCategory);
			}
		}
	}, [currentIndex, images, sortedCategories, selectedCategory, setSelectedCategory]);

	return (
		<>
			{/* Image Counter */}
			<GalleryImageCounter showImageName={true} />

			{/* Info Button */}
			<GalleryInfoButton />

			{/* Dots Navigation */}
			<GalleryDots />

			{/* Category Navigation */}
			{sortedCategories.length > 0 && (
				<GalleryCategories
					categories={sortedCategories}
					selectedCategory={selectedCategory}
					onCategoryChange={setSelectedCategory}
				/>
			)}
		</>
	);
}

export const GalleryLarge = React.memo(function GalleryLarge({
	images: imgs,
	categories,
	coverImage,
	projectId,
	type,
	shouldPrioritizeCoverImage = true,
	sizes = "80dvw",
}: UnitImageGalleryProps) {
	// Sort categories by unitSpaces priority and filter to only those with images
	const sortedCategories = (categories || defaultCategories)
		.filter(cat => imgs.some(img => img.space === cat.code))
		.sort((a, b) => {
			// Helper function to get category priority based on unitSpaces order
			const getCategoryPriority = (category: { name: string; code: string }): number => {
				const index = unitSpaces.indexOf(category.code);
				// If found in unitSpaces, return its index (lower index = higher priority)
				if (index !== -1) return index;

				// Other spaces (like 'others', 'amenities', 'parkings') get lower priority
				return 1000; // Large number to put them at the end
			};

			const priorityA = getCategoryPriority(a);
			const priorityB = getCategoryPriority(b);

			// If priorities are different, sort by priority
			if (priorityA !== priorityB) {
				return priorityA - priorityB;
			}

			// If same priority (both not in unitSpaces), sort by name alphabetically
			return a.name.localeCompare(b.name);
		});

	// Convert to ImageGalleryImage format and apply sorting
	const sortedImages = prepareGalleryImages({
		mediaList: imgs,
		coverImage,
		projectId,
		type,
		shouldPrioritizeCoverImage,
	});

	// Track selected category - select the first category from sorted list
	const [selectedCategory, setSelectedCategory] = React.useState<ImageGalleryCategory | undefined>(
		sortedCategories[0], // Select the first category in the sorted order
	);

	if (!sortedImages || sortedImages.length === 0) {
		return (
			<div className="flex items-center justify-center w-full h-96 bg-gray-100">
				<div className="text-center">
					<div className="w-16 h-16 bg-gray-300 mx-auto mb-4" />
					<p className="text-gray-500">No images to show</p>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white overflow-hidden h-full relative">
			<ImageGallery
				images={sortedImages}
				className="w-full h-full"
				showNavigationButtons={true}
				showImageCounter={false} // We'll use custom counter
				enableKeyboardNavigation={true}
				enableDoubleTapFullscreen={true}
				autoLoop={true}
				sizes={sizes}
			>
				<GalleryContent
					images={sortedImages}
					sortedCategories={sortedCategories}
					selectedCategory={selectedCategory}
					setSelectedCategory={setSelectedCategory}
				/>
			</ImageGallery>
		</div>
	);
});
